import { FunctionsHttpError } from "@supabase/supabase-js";

import { supabase } from "@/shared/lib/supabaseClient";
import { pessoasService, type Pessoa } from "@/core/services/pessoasService";
import type { Database, PessoaPerfil } from "@/shared/types/database.types";
import type { Sexo } from "@/shared/lib/sexo";

type PessoaUpdate = Database["public"]["Tables"]["pessoas"]["Update"];

export interface DadosCadastroPublico {
  cpf: string;
  nomeCompleto: string;
  telefone?: string;
  email: string;
  dataNascimento: string;
  sexo: Sexo;
  senha: string;
}

export interface DadosCriarUsuarioAdmin {
  cpf: string;
  nomeCompleto: string;
  telefone?: string;
  email: string;
  dataNascimento: string;
  sexo: Sexo;
  senha: string;
  perfil: PessoaPerfil;
}

/**
 * Fase 6 (etapa 6.2, contract): este service agora opera EXCLUSIVAMENTE
 * sobre `pessoas` (Núcleo) — leitura E escrita. A tabela `perfis` virou
 * detalhe interno do banco (espelho por trigger) e será removida na 6.3;
 * nenhuma tela ou service do frontend a referencia mais.
 *
 * Identificador usado pelas telas: `pessoa.id` (UUID próprio do Núcleo) —
 * NÃO confundir com `user_id` (= auth.users.id), que só interessa ao Auth.
 *
 * Vocabulário: o legado role 'admin'/'usuario' saiu do frontend; as telas
 * usam `pessoa.perfil` ('administrador'/'membro'). A única tradução restante
 * fica em `criarComoAdmin`, que mapeia perfil -> role no corpo enviado à
 * Edge Function `criar-usuario` (contrato dela, definido na Fase 5).
 */
export interface IgrejaDoCadastro {
  id: string;
  nome: string;
  codigo: string;
}

export const usuariosService = {
  /** Igreja do link de cadastro; `null` = link inválido (ou várias igrejas e nenhum código). */
  async igrejaDoCadastro(codigo?: string): Promise<IgrejaDoCadastro | null> {
    const { data, error } = await supabase.rpc("igreja_do_cadastro", { p_codigo: codigo ?? "" });
    if (error) throw new Error("Não foi possível verificar o link de cadastro.");
    return data?.[0] ?? null;
  },

  /**
   * Auto-cadastro público (sem sessão de admin). Cria a conta no Supabase
   * Auth e, em seguida, o próprio usuário recém-autenticado insere sua
   * pessoa — sempre como status='pendente' e perfil='membro' (garantido
   * também pela policy de RLS "Usuario autenticado cria a propria pessoa
   * pendente", migration 0016, não só por este código).
   *
   * Migration 0103 (link por igreja): a pessoa é gravada pela RPC
   * `cadastro_publico_criar_pessoa`, na igreja do link (`/cadastro/<codigo>`).
   * Sem código só vale enquanto a plataforma tem UMA igreja ativa (o banco
   * decide). O INSERT direto antigo só aceita a igreja padrão.
   *
   * Fase 4 — Segurança: a conta de Auth usa o e-mail REAL da pessoa como
   * identidade de login (não mais o e-mail técnico derivado do CPF). Isso é
   * necessário para o fluxo nativo de recuperação de senha do Supabase Auth
   * funcionar (ver authService.solicitarRedefinicaoSenha e migration 0011).
   * O login continua sendo feito por CPF — authService traduz CPF -> e-mail
   * real via RPC antes de autenticar.
   *
   * Graças ao trigger de confirmação automática de e-mail (migration 0004),
   * `signUp` sempre retorna uma `session` válida aqui.
   */
  async cadastrarPublico(dados: DadosCadastroPublico, codigoIgreja?: string): Promise<void> {
    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email: dados.email,
      password: dados.senha,
    });

    if (signUpError || !signUpData.user || !signUpData.session) {
      if (signUpError?.message?.toLowerCase().includes("already registered")) {
        throw new Error("Este e-mail já está cadastrado no sistema. Cada usuário deve ter um e-mail diferente.");
      }
      // BUG-09: para evitar enumeração de e-mail, o Supabase pode responder
      // signUp() SEM erro e SEM sessão quando o e-mail já existe — um
      // usuário "fantasma" com `identities: []` (nenhuma identidade nova
      // criada). Sem esse checagem, caía no genérico "verifique os dados",
      // que não avisa a causa real (e-mail duplicado).
      if (!signUpError && signUpData.user && signUpData.user.identities?.length === 0) {
        throw new Error("Este e-mail já está cadastrado no sistema. Cada usuário deve ter um e-mail diferente.");
      }
      throw new Error("Não foi possível criar sua conta. Confira o que você preencheu e tente de novo.");
    }

    await supabase.auth.setSession(signUpData.session);

    const { error: pessoaError } = await supabase.rpc("cadastro_publico_criar_pessoa", {
      p_codigo: codigoIgreja ?? "",
      p_cpf: dados.cpf,
      p_nome: dados.nomeCompleto,
      p_telefone: dados.telefone ?? "",
      p_email: dados.email,
      p_nascimento: dados.dataNascimento,
      p_sexo: dados.sexo,
    });

    if (pessoaError) {
      if (pessoaError.code === "23505") {
        await supabase.auth.signOut();
        throw new Error(
          "Este CPF já está cadastrado no sistema. Se você esqueceu sua senha, use \"Esqueci minha senha\" na tela de login.",
        );
      }
      if (pessoaError.message?.startsWith("Igreja não encontrada")) {
        await supabase.auth.signOut();
        throw new Error(pessoaError.message);
      }
      throw new Error("Sua conta foi criada, mas não foi possível salvar suas informações. Fale com o suporte.");
    }

    // Encerra a sessão recém-criada: o auto-cadastro não deve deixar a
    // pessoa "logada" enquanto o cadastro ainda está pendente de aprovação.
    await supabase.auth.signOut();
  },

  /**
   * Fase 6: leitura direto de `pessoas` (antes lia do espelho `perfis`).
   * Desde a tela de Visitantes: só pessoas COM login — visitante (user_id
   * null) vive no elo Visitantes, não aqui.
   */
  async listarTodos(): Promise<Pessoa[]> {
    return pessoasService.listarComLogin();
  },

  async listarPendentes(): Promise<Pessoa[]> {
    return pessoasService.listarPendentes();
  },

  /**
   * Criação de usuário pelo admin (já ativo, com perfil definido).
   *
   * Fase 5 (decisão D4): toda a operação roda na Edge Function
   * `criar-usuario` (supabase/functions/criar-usuario/index.ts), no servidor:
   * ela valida que o chamador é admin da plataforma (eh_admin_plataforma()),
   * cria a conta via Admin API (service_role, e-mail já confirmado) e insere
   * a pessoa em `pessoas`. Se o insert da pessoa falhar, a própria função
   * desfaz a conta de Auth — não sobra conta órfã.
   *
   * Nenhuma sessão do navegador é tocada: o `invoke` só envia o JWT do
   * admin no header Authorization para a função autorizar a chamada.
   *
   * Vocabulário: a Edge Function (contrato da Fase 5) recebe `role`
   * ('admin'/'usuario'); aqui traduzimos a partir de `perfil` do Núcleo.
   */
  async criarComoAdmin(dados: DadosCriarUsuarioAdmin): Promise<void> {
    const { error } = await supabase.functions.invoke("criar-usuario", {
      body: {
        cpf: dados.cpf,
        nomeCompleto: dados.nomeCompleto,
        telefone: dados.telefone ?? null,
        email: dados.email,
        dataNascimento: dados.dataNascimento,
        sexo: dados.sexo,
        senha: dados.senha,
        role: dados.perfil === "administrador" ? "admin" : "usuario",
      },
    });

    if (!error) return;

    // Para respostas não-2xx o invoke devolve FunctionsHttpError; o corpo
    // JSON da função traz um `code` estável (contrato documentado no
    // index.ts da função) que traduzimos em mensagem amigável.
    let codigo: string | undefined;
    if (error instanceof FunctionsHttpError) {
      try {
        const corpo = (await error.context.json()) as { code?: string };
        codigo = corpo.code;
      } catch {
        // Corpo não-JSON (ex.: falha de gateway) — cai na mensagem genérica.
      }
    }

    if (codigo === "email_duplicado") {
      throw new Error("Este e-mail já está cadastrado no sistema. Cada usuário deve ter um e-mail diferente.");
    }
    if (codigo === "cpf_duplicado") {
      throw new Error(
        "Este CPF já está cadastrado no sistema. Se o usuário esqueceu a senha, oriente-o a usar \"Esqueci minha senha\" na tela de login.",
      );
    }
    if (codigo === "nao_autorizado" || codigo === "nao_autenticado") {
      throw new Error("Sua sessão não tem permissão para criar usuários. Faça login novamente.");
    }
    throw new Error("Não foi possível criar a conta. Confira o que foi preenchido e tente de novo.");
  },

  /**
   * Atualização de dados pelo admin. Fase 6: as telas passam `pessoa.id`
   * (id próprio do Núcleo) e campos no vocabulário de `pessoas` — sem
   * tradução de role/perfil, sem localizar por user_id.
   *
   * F2-42 (25/07/2026): quando o e-mail está entre os campos alterados, a
   * atualização precisa passar pela Edge Function `atualizar-usuario` — só
   * ela tem a service_role key pra também trocar o e-mail no Supabase Auth
   * (auth.users). Sem isso, o e-mail mudava só em `pessoas` e o login (que
   * resolve CPF -> pessoas.email -> Auth via buscar_email_por_cpf +
   * signInWithPassword) ficava travado com o e-mail antigo — bug real
   * encontrado em produção em 25/07/2026. Trocas que NÃO tocam email (ex.:
   * só perfil, pela etiqueta clicável na listagem) continuam indo direto
   * pra `pessoas` — não precisam da service_role.
   */
  async atualizar(id: string, dados: PessoaUpdate): Promise<void> {
    if (dados.email === undefined) {
      return pessoasService.atualizar(id, dados);
    }

    const { error } = await supabase.functions.invoke("atualizar-usuario", {
      body: {
        id,
        nomeCompleto: dados.nome_completo,
        telefone: dados.telefone ?? null,
        email: dados.email,
        dataNascimento: dados.data_nascimento,
        // null/ausente = a função não mexe no sexo já salvo (a edição do admin só troca, não limpa).
        sexo: dados.sexo ?? null,
      },
    });

    if (!error) return;

    // Mesmo padrão de tradução de erro de criarComoAdmin: o corpo JSON traz
    // um `code` estável (contrato documentado no index.ts da função).
    let codigo: string | undefined;
    if (error instanceof FunctionsHttpError) {
      try {
        const corpo = (await error.context.json()) as { code?: string };
        codigo = corpo.code;
      } catch {
        // Corpo não-JSON (ex.: falha de gateway) — cai na mensagem genérica.
      }
    }

    if (codigo === "email_duplicado") {
      throw new Error("Este e-mail já está em uso por outra conta.");
    }
    if (codigo === "pessoa_nao_encontrada") {
      throw new Error("Usuário não encontrado.");
    }
    if (codigo === "nao_autorizado" || codigo === "nao_autenticado") {
      throw new Error("Sua sessão não tem permissão para editar usuários. Faça login novamente.");
    }
    throw new Error("Não foi possível atualizar o usuário.");
  },

  /**
   * Auto-edição no Meu perfil (02/11/2026): a própria pessoa altera SÓ telefone e data de
   * nascimento. A garantia está no banco (migration 0115): qualquer outra coluna alterada pela
   * própria pessoa é recusada pelo trigger `protege_campos_sensiveis_pessoas`.
   */
  async atualizarMeusDados(
    id: string,
    dados: { telefone: string | null; dataNascimento: string },
  ): Promise<void> {
    const { error } = await supabase
      .from("pessoas")
      .update({ telefone: dados.telefone, data_nascimento: dados.dataNascimento })
      .eq("id", id);
    if (error) throw new Error("Não foi possível salvar seus dados. Tente de novo.");
  },

  /** Pendente -> ativo. Usado para aprovar um cadastro novo. */
  async aprovar(id: string): Promise<void> {
    return pessoasService.aprovar(id);
  },

  /** Ativo -> inativo. Usado para desabilitar um usuário já aprovado. */
  async inativar(id: string): Promise<void> {
    return pessoasService.inativar(id);
  },

  /** Inativo -> ativo. Usado para reabilitar um usuário que tinha sido inativado. */
  async reativar(id: string): Promise<void> {
    return pessoasService.reativar(id);
  },
};

export type { Pessoa };
